import { Avatar } from "@dash/avatar";
import { QuerySkeleton } from "@dash/skeleton";
import { Tile } from "@dash/tile";
import { useTheme } from "@emotion/react";
import { useProfiles } from "@megarax/features";
import { useNavigate } from "react-router-dom-v5-compat";

import { CustomerDoc, Interaction } from "@megaron/crm-contracts";
import { UserDoc } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import { ResourceId } from "@megaron/resource";
import { getMegaraxPath } from "@megaron/megarax-uri";

export const LatestMentions = () => {
  const theme = useTheme();

  const mentionsQuery = useClientManager("crm").customerMentionedInteractions().useQuery({});

  const { profiles } = useProfiles();

  const ownerProfiles = profiles(mentionsQuery.data?.map((interaction) => interaction.ownerEmail) ?? []);

  const customers = useClientManager("docs")
    .retrieveDocs()
    .useQuery({
      docIds: mentionsQuery.data?.map((interaction) => ResourceId("customer", interaction.customerId)) ?? [],
    });

  return (
    <QuerySkeleton query={mentionsQuery}>
      {(mentions) => (
        <div css={{ display: "flex", flexDirection: "column", gap: "0.675rem" }}>
          {mentions.length > 0 ? (
            mentions
              .slice(0, 5)
              .map((mention) => (
                <MentionTile
                  mention={mention}
                  key={mention.uuid}
                  ownerProfile={ownerProfiles?.find((profile) => profile.email === mention.ownerEmail)}
                  customerName={
                    customers.data?.find((customer) => (customer as CustomerDoc).uuid === mention.customerId)?.name ||
                    undefined
                  }
                />
              ))
          ) : (
            <div css={{ fontSize: "0.875rem", color: theme.colors.secondaryText }}>Brak oznaczeń</div>
          )}
        </div>
      )}
    </QuerySkeleton>
  );
};

const MentionTile: React.FC<{
  mention: Interaction;
  ownerProfile: UserDoc | undefined;
  customerName: string | undefined;
}> = ({ mention, ownerProfile, customerName }) => {
  const theme = useTheme();

  const navigate = useNavigate();

  const handleMentionClick = () => {
    const path = `${getMegaraxPath.uri("megarax://crm/customers/id/{id}", { id: mention.customerId })}?tab=historia`;

    navigate(path, { state: { scrollTo: `interaction-${mention.uuid}` } });
  };

  return (
    <Tile onClick={handleMentionClick} css={{ cursor: "pointer" }}>
      <div css={{ display: "flex", flexDirection: "column", gap: "0.675rem" }}>
        <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "space-between" }}>
          <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Avatar src={ownerProfile?.profilePictureUrl} size="18px" />
            <span css={{ fontSize: "0.875rem", color: theme.colors.secondaryText }}>{ownerProfile?.name ?? "-"}</span>
          </div>
          <span css={{ fontSize: "0.75rem", color: theme.colors.secondaryText }}>
            {mention.interactionDate.toLocaleDateString()}
          </span>
        </div>
        <p css={{ margin: 0, fontSize: "1rem", color: "black" }}>{mention.message}</p>
        <span css={{ fontSize: "0.875rem", color: theme.colors.primary }}>
          Dotyczy klienta: {customerName || mention.customerId}
        </span>
      </div>
    </Tile>
  );
};
